import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import {
  PencilFill, CheckLg, XLg, PlusCircle, Trash, TrophyFill,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateWinnersListSection } from '../../services/contentService';
import './WinnersListBlock.scss';

function defaultEntry() {
  return {
    trackLabel: 'Season 1 — Track Name',
    winnerName: 'Winning Team',
    description: 'One- or two-sentence summary of the winning solution.',
    linkUrl: '',
  };
}

export function defaultWinnersListData() {
  return {
    title: 'Past winners',
    entries: [defaultEntry()],
  };
}

// Read-only renderer — used by both the public site and the admin view mode.
// Each entry can optionally be a link; when linkUrl is set, the whole row
// becomes clickable and opens in a new tab.
export function WinnersListView({ data }) {
  const entries = Array.isArray(data?.entries) ? data.entries : [];
  return (
    <div className="winners-list-block">
      {data?.title ? (
        <h2 className="winners-list-block__title">{data.title}</h2>
      ) : null}
      <div className="winners-list-block__entries">
        {entries.map((entry, idx) => {
          const inner = (
            <div className="winners-list-block__entry">
              <div className="winners-list-block__icon-wrap">
                <TrophyFill className="winners-list-block__icon" />
              </div>
              <div className="winners-list-block__body">
                {entry.trackLabel ? (
                  <div className="winners-list-block__track-label">{entry.trackLabel}</div>
                ) : null}
                {entry.winnerName ? (
                  <div className="winners-list-block__winner-name">{entry.winnerName}</div>
                ) : null}
                {entry.description ? (
                  <div className="winners-list-block__description">{entry.description}</div>
                ) : null}
              </div>
            </div>
          );
          return entry.linkUrl ? (
            <a
              key={idx}
              href={entry.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="winners-list-block__entry-link"
            >
              {inner}
            </a>
          ) : (
            <div key={idx}>{inner}</div>
          );
        })}
      </div>
    </div>
  );
}

function WinnersListBlock({
  pageId,
  section,
  onMoveUp,
  onMoveDown,
  onDelete,
  isFirstSection,
  isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultWinnersListData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setData(section?.data || defaultWinnersListData());
  }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateWinnersListSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving winners list:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    setData(section?.data || defaultWinnersListData());
    setIsEditing(false);
  }

  function updateTitle(value) {
    setData((prev) => ({ ...prev, title: value }));
  }

  function updateEntry(idx, patch) {
    setData((prev) => {
      const next = { ...prev, entries: [...(prev.entries || [])] };
      next.entries[idx] = { ...next.entries[idx], ...patch };
      return next;
    });
  }

  function addEntry() {
    setData((prev) => ({ ...prev, entries: [...(prev.entries || []), defaultEntry()] }));
  }

  function removeEntry(idx) {
    setData((prev) => {
      const next = { ...prev, entries: [...(prev.entries || [])] };
      next.entries.splice(idx, 1);
      return next;
    });
  }

  if (isEditing) {
    return (
      <div className="winners-list-block winners-list-block--editing">
        <Form.Group className="mb-3">
          <Form.Label>Section title</Form.Label>
          <Form.Control
            type="text"
            value={data.title || ''}
            onChange={(e) => updateTitle(e.target.value)}
          />
        </Form.Group>

        {(data.entries || []).map((entry, idx) => (
          <div key={idx} className="winners-list-block__editor-entry">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Entry {idx + 1}</strong>
              <Button variant="outline-danger" size="sm" onClick={() => removeEntry(idx)}>
                <Trash /> Remove
              </Button>
            </div>
            <Row className="g-2">
              <Col md={6}>
                <Form.Label>Track label</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Season 1 — ATCO Spacelab: Smart Charging"
                  value={entry.trackLabel || ''}
                  onChange={(e) => updateEntry(idx, { trackLabel: e.target.value })}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Winner name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="AngelQ"
                  value={entry.winnerName || ''}
                  onChange={(e) => updateEntry(idx, { winnerName: e.target.value })}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={entry.description || ''}
                  onChange={(e) => updateEntry(idx, { description: e.target.value })}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Link URL (opens in a new tab)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="https://… or /page/tracks/…"
                  value={entry.linkUrl || ''}
                  onChange={(e) => updateEntry(idx, { linkUrl: e.target.value })}
                />
              </Col>
            </Row>
          </div>
        ))}

        <Button variant="outline-primary" size="sm" onClick={addEntry} className="mb-3">
          <PlusCircle /> Add entry
        </Button>

        <div className="winners-list-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>
            ) : (
              <><CheckLg /> Save</>
            )}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
            <XLg /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="winners-list-block winners-list-block--view">
      <WinnersListView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button
            variant="outline-secondary"
            size="sm"
            className="edit-btn"
            onClick={() => setIsEditing(true)}
          >
            <PencilFill /> Edit entries
          </Button>
          <SectionControls
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDelete={onDelete}
            isFirstSection={isFirstSection}
            isLastSection={isLastSection}
          />
        </div>
      </RoleGuard>
    </div>
  );
}

export default WinnersListBlock;
